import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../components/ui/breadcrumb";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

import {
  AlertCircle,
  RefreshCw,
  Globe,
  Plus,
  ArrowLeft,
  Trash2,
} from "lucide-react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { projectId } from "../utils/supabase/info";
import { getValidAccessToken } from "../utils/auth";
import { toast } from "sonner";
import { useWebsite } from "../contexts/WebsiteContext";

// Mock data for charts (will be replaced with real data later)
const errorData = [
  { name: "Critical", count: 12, color: "#ef4444" },
  { name: "Serious", count: 28, color: "#f97316" },
  { name: "Moderate", count: 45, color: "#eab308" },
  { name: "Minor", count: 67, color: "#3b82f6" },
];

const recentPages = [
  {
    id: 1,
    title: "Homepage",
    url: "/index.html",
    analyzed: "2 hours ago",
    errors: 8,
    warnings: 15,
  },
  {
    id: 2,
    title: "About Us",
    url: "/about.html",
    analyzed: "3 hours ago",
    errors: 3,
    warnings: 7,
  },
  {
    id: 3,
    title: "Contact",
    url: "/contact.html",
    analyzed: "5 hours ago",
    errors: 5,
    warnings: 12,
  },
  {
    id: 4,
    title: "Products",
    url: "/products.html",
    analyzed: "1 day ago",
    errors: 15,
    warnings: 23,
  },
];


type UserProfile = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
};


export default function Dashboard() {
  
  const navigate = useNavigate();

  const { websiteId } = useParams<{ websiteId?: string, cache?: string }>();
  
  const [searchParams] = useSearchParams();

  const cache = searchParams.get("cache");

  const {
    websites,
    isLoading: websitesLoading,
    setSelectedWebsite,
    refreshWebsites,
  } = useWebsite();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [websiteToDelete, setWebsiteToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Find the specific website if websiteId is in URL
  const currentWebsite = websiteId
    ? websites.find((w) => w.id === Number(websiteId))
    : null;

  // Determine if we're in "all websites" mode or "specific website" mode
  const isAllWebsitesMode = !websiteId;
  const isSpecificWebsiteMode = !!websiteId && !!currentWebsite;



  useEffect(() => {
    let mounted = true;

    const fetchUserProfile = async () => {
      try {
        // Check for cached profile first
        const cachedProfile = localStorage.getItem("userProfile");
        const cacheTimestamp = localStorage.getItem("userProfileTimestamp");

        // Use cache if it's less than 5 minutes old
        if (cachedProfile && cacheTimestamp) {
          const cacheAge = Date.now() - parseInt(cacheTimestamp);
          if (cacheAge < 5 * 60 * 1000) {
            // 5 minutes
            if (mounted) {
              setUserProfile(JSON.parse(cachedProfile));
            }
            return;
          }
        }

        const startTime = Date.now();

        // Get valid access token
        const accessToken = await getValidAccessToken();
        const tokenTime = Date.now() - startTime;

        if (!accessToken) {
          if (cachedProfile && mounted) {
            setUserProfile(JSON.parse(cachedProfile));
          }
          return;
        }

        // Fetch user profile with 10-second timeout (increased for slower connections)
        const controller = new AbortController();
        const fetchTimeoutId = setTimeout(() => {
          controller.abort();
        }, 10000);

        try {
          const fetchStartTime = Date.now();
          const profileResponse = await fetch(
            `https://${projectId}.supabase.co/functions/v1/make-server-3b83895f/profile`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
              signal: controller.signal,
            }
          );

          clearTimeout(fetchTimeoutId);
          const fetchTime = Date.now() - fetchStartTime;

          if (!mounted) return;

          if (profileResponse.ok) {
            const profileData = await profileResponse.json();
            if (mounted && profileData.profile) {
              setUserProfile(profileData.profile);
              // Cache the profile data
              localStorage.setItem(
                "userProfile",
                JSON.stringify(profileData.profile)
              );
              localStorage.setItem(
                "userProfileTimestamp",
                Date.now().toString()
              );
            }
          } else if (profileResponse.status === 401) {
            if (cachedProfile && mounted) {
              setUserProfile(JSON.parse(cachedProfile));
            }
          } else {
            if (cachedProfile && mounted) {
              setUserProfile(JSON.parse(cachedProfile));
            }
          }
        } catch (fetchError: any) {
          clearTimeout(fetchTimeoutId);

          if (fetchError.name === "AbortError") {
          } else {
          }

          // Use cached profile as fallback
          if (cachedProfile && mounted) {
            setUserProfile(JSON.parse(cachedProfile));
          }
        }
      } catch (error) {
      }
    };

    // Fetch profile in background (non-blocking)
    fetchUserProfile();

    return () => {
      mounted = false;
    };
  }, [navigate]);

  useEffect(() => {
    if (cache && cache === "false") {
      refreshWebsites();
    }
  }, [cache]);

  // Sync selected website with URL parameter
  useEffect(() => {
    if (currentWebsite) {
      setSelectedWebsite(currentWebsite);
    } else {
      setSelectedWebsite(null); // Reset to null for all websites mode
    }
  }, [currentWebsite, setSelectedWebsite]);

  const handleDeleteWebsite = async () => {
    if (!websiteToDelete) return;

    setIsDeleting(true);
    try {
      const accessToken = await getValidAccessToken();
      if (!accessToken) {
        toast.error("Please login to delete website");
        navigate("/login");
        return;
      }

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-3b83895f/website/${websiteToDelete.id}`,
        // {
        //   method: "DELETE",
        //   headers: {
        //     Authorization: `Bearer ${accessToken}`,
        //   },
        // }
        {
          method: "PATCH", // Change to PATCH for update
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ isRemoved: true }), // Update isRemoved
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete website");
      }

      toast.success(`${websiteToDelete.name} has been deleted successfully`);

      // Refresh the website list
      await refreshWebsites();

      // If we're viewing the deleted website, navigate back to dashboard
      if (websiteId === websiteToDelete.id) {
        navigate("/dashboard");
      }

      setDeleteDialogOpen(false);
      setWebsiteToDelete(null);
    } catch (error: any) {
      toast.error(
        error.message || "Failed to delete website. Please try again."
      );
    } finally {
      setIsDeleting(false);
    }
  };

  // Only wait for websites to load (profile loads in background)
  if (websitesLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-center h-96">
          <div className="text-center space-y-4">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
            <div>
              <p className="text-muted-foreground mb-2">
                Loading your websites...
              </p>
              <p className="text-xs text-muted-foreground">
                This should only take a few seconds
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If no websites found, show enhanced onboarding empty state
  if (!websites || websites.length === 0) {
    return (
      <div className="p-6 space-y-6 max-w-5xl mx-auto">
        {/* <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbPage>Dashboard</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb> */}

        {userProfile && (
          <div className="text-center mb-8 mt-4">
            <h1 className="text-4xl mb-2">
              Welcome to AxOrigin, {userProfile.first_name}! 🎉
            </h1>
            <p className="text-lg text-muted-foreground">
              Let's get you started on your accessibility journey
            </p>
          </div>
        )}

        {/* Main Empty State Card */}
        <Card className="border-2 border-dashed border-primary/30 bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardContent className="flex flex-col items-center justify-center py-16 px-6">
            <div className="rounded-full bg-primary/10 p-6 mb-6">
              <Globe className="h-16 w-16 text-primary" />
            </div>
            <h2 className="text-2xl mb-3">No websites added yet</h2>
            {/*  <p className="text-muted-foreground mb-8 text-center max-w-xl leading-relaxed">
              Start monitoring your website's accessibility compliance by adding
              your first website. You'll be able to integrate our widget, scan
              pages, track issues, and ensure WCAG 2.1 and EN 301 549
              compliance.
            </p> */}
            <p className="text-muted-foreground mb-8 text-center max-w-xl leading-relaxed">
              Start your website's accessibility compliance by adding your first
              website. You'll be able to integrate our widget, and ensure
              accessibility compliance.
            </p>
            <Button
              size="lg"
              className="bg-primary-gradient text-lg px-8 py-6 h-auto"
              onClick={() => {
                // Dispatch custom event to open Add Website dialog in TopNav
                window.dispatchEvent(new Event("openAddWebsiteDialog"));
              }}
            >
              <Plus className="mr-2 h-5 w-5" />
              Add Your First Website
            </Button>
          </CardContent>
        </Card>

        {/* Feature Cards */}
        {/*  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          <Card>
            <CardHeader>
              <div className="rounded-full bg-blue-100 w-12 h-12 flex items-center justify-center mb-3">
                <Code2 className="h-6 w-6 text-blue-600" />
              </div>
              <CardTitle className="text-lg">Easy Integration</CardTitle>
              <CardDescription>
                Add our accessibility widget to your website with just a few
                lines of code
              </CardDescription>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader>
              <div className="rounded-full bg-green-100 w-12 h-12 flex items-center justify-center mb-3">
                <AlertCircle className="h-6 w-6 text-green-600" />
              </div>
              <CardTitle className="text-lg">Automated Scanning</CardTitle>
              <CardDescription>
                Continuously monitor your pages for accessibility issues and
                compliance
              </CardDescription>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader>
              <div className="rounded-full bg-purple-100 w-12 h-12 flex items-center justify-center mb-3">
                <FileText className="h-6 w-6 text-purple-600" />
              </div>
              <CardTitle className="text-lg">Detailed Reports</CardTitle>
              <CardDescription>
                Get comprehensive reports with WCAG 2.1 and EN 301 549 criteria
                references
              </CardDescription>
            </CardHeader>
          </Card>
        </div> */}
      </div>
    );
  }

  // If websiteId is provided but website not found (only check after websites have loaded)
  if (websiteId && !currentWebsite && websites.length > 0) {
    return (
      <div className="p-6 space-y-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Website Not Found</AlertTitle>
          <AlertDescription>
            The website you're looking for doesn't exist or you don't have
            access to it.
          </AlertDescription>
        </Alert>
        <Button onClick={() => navigate("/dashboard")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to All Websites
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            {isSpecificWebsiteMode ? (
              <BreadcrumbLink asChild>
                <Link to="/dashboard">Dashboard</Link>
              </BreadcrumbLink>
            ) : (
              <BreadcrumbPage>Dashboard</BreadcrumbPage>
            )}
          </BreadcrumbItem>
          {isSpecificWebsiteMode && (
            <>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{currentWebsite.website_name}</BreadcrumbPage>
              </BreadcrumbItem>
            </>
          )}
        </BreadcrumbList>
      </Breadcrumb>

      {/* Back Button for Specific Website Mode */}
      {isSpecificWebsiteMode && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate("/dashboard")}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to All Websites
        </Button>
      )}

      {/* Welcome Message */}
      {userProfile && isAllWebsitesMode && (
        <div className="mb-4">
          <h1 className="text-3xl">Welcome back, {userProfile.first_name}!</h1>
          <p className="text-muted-foreground">
            Here's an overview of all your websites
          </p>
        </div>
      )}

      {/* Website-Specific Header */}
      {isSpecificWebsiteMode && (
        <div className="mb-4">
          <h1 className="text-3xl">{currentWebsite.website_name}</h1>
          <p className="text-muted-foreground">{currentWebsite.domain}</p>
        </div>
      )}

      {/* Plan Banner */}
      {isSpecificWebsiteMode && (
        <Alert className="border-blue-200 bg-blue-50">
          <AlertCircle className="h-4 w-4 text-blue-600" />
          <AlertTitle className="text-blue-900">
            {isSpecificWebsiteMode
              ? `${(currentWebsite.plan_id || "free").charAt(0).toUpperCase() +
              (currentWebsite.plan_id || "free").slice(1)
              } Plan Active`
              : "Free Plan Active"}
          </AlertTitle>
          <AlertDescription className="text-blue-800">
            {isSpecificWebsiteMode
              ? `This website is on the ${(currentWebsite.plan_id || "free").charAt(0).toUpperCase() +
              (currentWebsite.plan_id || "free").slice(1)
              } plan.`
              : "You're currently on the Free plan with limited features. Trial expires in 14 days."}

            {currentWebsite.platform !== "wix" && (
              <Button
                variant="link"
                className="p-0 ml-2 h-auto text-blue-600"
                // onClick={() => navigate("/manage-plan")}
                onClick={() => navigate(`/manage-plan/${currentWebsite.id}`)}
              >
                Manage Plan →
              </Button>
            )}

          </AlertDescription>
        </Alert>
      )}

      {/* ALL WEBSITES MODE - Show website grid */}
      {isAllWebsitesMode && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {websites
            .filter((website) => !website.isRemoved)
            .map((website) => {
              const planId = website.plan_id || "free";
              const billingCycle = website.paymentCycle;
              const isEnterprise = planId === "enterprise";

              // Highest plan = Enterprise YEARLY
              const isHighestPlan =
                planId === "enterprise" && billingCycle === "yearly";

              return (
                <Card
                  key={website.id}
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/dashboard/${website.id}`)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg">
                          {website.website_name}
                        </CardTitle>
                        <CardDescription className="truncate">
                          {website.domain}
                        </CardDescription>
                      </div>
                      <Badge className="bg-green-600 shrink-0">Active</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        Last Scan
                      </div>
                      <p className="text-sm">Not scanned yet</p>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <FileText className="h-4 w-4" />
                        Pages
                      </div>
                      <p className="text-sm">0 / 50</p>
                    </div>
                  </div> */}

                    <div className="pt-3 border-t space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                          Current Plan
                        </span>
                        <Badge
                          variant={website.isFree ? "secondary" : "default"}
                          className={`capitalize ${website.isFree ? "" : "bg-primary-gradient"
                            }`}
                        >
                          {planId}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                          Billing Cycle
                        </span>
                        {/* {website.isFree ? (
                        "-"
                      ) : ( */}
                        <Badge
                          variant="default"
                          className={`capitalize ${planId === "free" ? "" : "bg-primary-gradient"
                            }`}
                        >
                          {billingCycle === null ? "NA" : billingCycle}
                        </Badge>
                        {/* )} */}
                      </div>

                      {!isHighestPlan && (
                        <Button
                          size="sm"
                          className="w-full bg-primary-gradient"
                          onClick={(e) => {
                            e.stopPropagation();
                            // navigate('/manage-plan');
                            navigate(`/manage-plan/${website.id}`);
                          }}
                        >
                          Upgrade Plan
                        </Button>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/dashboard/${website.id}`);
                        }}
                      >
                        View Details →
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:bg-destructive hover:text-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          setWebsiteToDelete({
                            id: website.id.toString(),
                            name: website.website_name
                              ? website.website_name
                              : "",
                          });
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
        </div>
      )}

      {/* SPECIFIC WEBSITE MODE - Show detailed stats */}
      {/*  {isSpecificWebsiteMode && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Status</CardDescription>
              <CardTitle className="text-2xl">
                <Badge className="bg-green-600">Active</Badge>
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Last Scan</CardDescription>
              <CardTitle className="text-2xl">Never</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Total Pages</CardDescription>
              <CardTitle className="text-2xl">0</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Issues Found</CardDescription>
              <CardTitle className="text-2xl">0</CardTitle>
            </CardHeader>
          </Card>
        </div>
      )} */}

      {/* Statistics Section - Different for each mode */}
      {/* {isAllWebsitesMode && (
        <Card>
          <CardHeader>
            <CardTitle>Overall Statistics</CardTitle>
            <CardDescription>Aggregate data across all {websites.length} website{websites.length !== 1 ? 's' : ''}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Globe className="h-4 w-4" />
                  Total Websites
                </div>
                <p className="text-2xl">{websites.length}</p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <FileText className="h-4 w-4" />
                  Total Pages
                </div>
                <p className="text-2xl">0</p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <AlertCircle className="h-4 w-4" />
                  Total Issues
                </div>
                <p className="text-2xl">0</p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <RefreshCw className="h-4 w-4" />
                  Active Scans
                </div>
                <p className="text-2xl">0</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )} */}

      {/* {isSpecificWebsiteMode && (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle>Website Information</CardTitle>
                <CardDescription>Configuration and settings for {currentWebsite.website_name}</CardDescription>
              </div>
              <div className="flex gap-2">
                {currentWebsite.plan_id !== 'enterprise' && (
                  <Button
                    className="bg-primary-gradient"
                    onClick={() => navigate('/manage-plan')}
                  >
                    <Zap className="mr-2 h-4 w-4" />
                    Upgrade Plan
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                  onClick={() => {
                    setWebsiteToDelete({ id: currentWebsite.id.toString(), name: currentWebsite.website_name ? currentWebsite.website_name : '' });
                    setDeleteDialogOpen(true);
                  }}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Website
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Domain</p>
                <p>{currentWebsite.domain}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Added On</p>
                <p>{new Date(currentWebsite.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Current Plan</p>
                <Badge variant={currentWebsite.plan_id === 'free' ? "secondary" : "default"}>
                  {(currentWebsite.plan_id || 'free').charAt(0).toUpperCase() + (currentWebsite.plan_id || 'free').slice(1)}
                </Badge>
              </div>
              <div >
                <p className="text-sm text-muted-foreground mb-1">Billing Cycle</p>
                <Badge variant="default" >
                  {currentWebsite.paymentCycle === null ? 'NA' : currentWebsite.paymentCycle}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Accessibility Score</p>
                <p>Not scanned yet</p>
              </div>
            </div>

            <div className="pt-4 border-t flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/widget-integration')}
              >
                <Code2 className="mr-2 h-4 w-4" />
                Widget Code
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/widget-settings')}
              >
                <Settings className="mr-2 h-4 w-4" />
                Widget Settings
              </Button>
              <Button 
                className="bg-primary-gradient"
                onClick={() => navigate('/automated-tests')}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Run Scan
              </Button>
            </div>
          </CardContent>
        </Card>
      )} */}

      {/* Scan Information */}
      {/* <Card>
        <CardHeader>
          <CardTitle>Scan Information</CardTitle>
          <CardDescription>Overview of discovered and analyzed pages</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm">Total Pages Discovered</p>
                <p className="text-2xl">0</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm">Pages Analyzed</p>
                <p className="text-2xl">0</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm">Accessibility Score</p>
                <p className="text-2xl">-</p>
              </div>
            </div>
            <div className="pt-4">
              <Button className="w-full bg-primary-gradient" onClick={() => navigate('/automated-tests')}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Run First Scan
              </Button>
            </div>
          </div>
        </CardContent>
      </Card> */}

      {/* Error Distribution - Show for both modes */}
      {/* <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Error Distribution</CardTitle>
              <CardDescription>
                {isSpecificWebsiteMode
                  ? `Breakdown by severity level for ${currentWebsite.website_name} (Demo Data)`
                  : `Breakdown by severity level across all websites (Demo Data)`
                }
              </CardDescription>
            </div>
            <Button variant="outline" size="sm">
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={errorData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#2563eb" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card> */}

      {/* Recent Pages Analyzed - Show for both modes */}
      {/* <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Recent Pages Analyzed</CardTitle>
              <CardDescription>
                {isSpecificWebsiteMode
                  ? `Latest accessibility scans for ${currentWebsite.website_name} (Demo Data)`
                  : 'Latest accessibility scans across all websites (Demo Data)'
                }
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/pages')}>
              View All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentPages.map((page) => (
              <div key={page.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <p className="font-medium">{page.title}</p>
                  <p className="text-sm text-muted-foreground">{page.url}</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-sm text-destructive">{page.errors} errors</p>
                    <p className="text-sm text-orange-600">{page.warnings} warnings</p>
                  </div>
                  <div className="text-sm text-muted-foreground">{page.analyzed}</div>
                  <Button variant="outline" size="sm" onClick={() => navigate('/pages')}>
                    View
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card> */}

      {/* Quick Actions */}
      {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/widget-integration')}>
          <CardHeader>
            <CardTitle className="text-base">Widget Integration</CardTitle>
            <CardDescription>Add accessibility widget to your site</CardDescription>
          </CardHeader>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/automated-tests')}>
          <CardHeader>
            <CardTitle className="text-base">Run Automated Test</CardTitle>
            <CardDescription>Start a new accessibility scan</CardDescription>
          </CardHeader>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/documents')}>
          <CardHeader>
            <CardTitle className="text-base">View Reports</CardTitle>
            <CardDescription>Download compliance documents</CardDescription>
          </CardHeader>
        </Card>
      </div> */}

      {/* Delete Website Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Website</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{" "}
              <strong>{websiteToDelete?.name}</strong>? This action cannot be
              undone. All data associated with this website including scans,
              reports, and settings will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteWebsite();
              }}
              disabled={isDeleting}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <RefreshCw className="mr-1 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-1 h-4 w-4" />
                  Delete Website
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
